import { LitElement, html, nothing } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { DataQueryController } from "ui-common";
import { getSocialIcon } from "ui-components/helpers/socialIconMapping";
import "ui-components/skeleton-circle";
import "ui-components/user-theme-button/user-theme-button";
import { getAssetUrl } from "helpers/assetHelpers";

import { styles } from "./user-background-image.css";

import { profileDataQueryOptions } from "../../queries/profile-data";
import { t } from "../../translations/i18n";
import "../user-avatar/user-avatar";
import { parseSocialMediaUrls, type ParsedSocialLink } from "ui-common/socialMediaConfig";

@customElement("user-background-image")
export class UserBackgroundImage extends LitElement {
  static override styles = styles;

  @property({ type: String })
  primaryColor?: string;

  @property({ type: String })
  secondaryColor?: string;

  @property({ type: Array })
  socialLinks?: string[];

  @property({ type: String })
  userHandle?: string;

  @property({ type: Boolean })
  onboarding?: boolean = false;

  @state()
  backgroundImageUrl?: string;

  @state()
  isVideoBackground: boolean = false;

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);

  private get parsedSocialLinks(): ParsedSocialLink[] {
    // Priority 1: Use passed socialLinks property (real-time data from parent)
    const links = this.socialLinks ?? this.profileDataQuery.result.data?.socialLinks;
    if (!links) {
      return [];
    }
    return parseSocialMediaUrls(links);
  }

  private get currentThemeColors(): { primary: string; secondary: string } {
    // Priority 1: Use colors passed as properties (from parent's dynamic state)
    if (this.primaryColor && this.secondaryColor) {
      return {
        primary: this.primaryColor,
        secondary: this.secondaryColor,
      };
    }

    // Priority 2: Fallback to profile data (server state)
    const profileData = this.profileDataQuery.result.data;

    // Priority 2a: Selected theme colors (if a theme is selected)
    if (profileData?.theme) {
      return {
        primary: profileData.theme.primaryColor,
        secondary: profileData.theme.secondaryColor,
      };
    }

    // Priority 2b: Custom colors (if no theme is selected but custom colors exist)
    if (profileData?.customColors) {
      return {
        primary: profileData.customColors.primary,
        secondary: profileData.customColors.secondary,
      };
    }

    // Fallback: Default colors
    return {
      primary: "#FF5733",
      secondary: "#33C1FF",
    };
  }

  private renderSocialLinks() {
    const maxVisible = 4;

    // Loading state - show 3 skeleton circles (only if using internal query and no socialLinks prop)
    if (this.profileDataQuery?.result.isPending) {
      return html`
        <div class="social-links">
          ${Array.from(
            { length: maxVisible },
            () => html`
              <div class="social-link-button">
                <skeleton-circle elevation="1"></skeleton-circle>
              </div>
            `,
          )}
        </div>
      `;
    }

    if (this.profileDataQuery?.result.error) {
      return html`<div class="social-links"></div>`;
    }

    const socialLinks = this.parsedSocialLinks;
    const visibleLinks = socialLinks.slice(0, maxVisible);
    const remainingCount = socialLinks.length - maxVisible;
    const themeColors = this.currentThemeColors;

    return html`
      <div class="social-links">
        ${visibleLinks.map((link) =>
          link.platform !== "other"
            ? html`
                <user-theme-button
                  .circle=${true}
                  .primaryColor=${themeColors.secondary}
                  .secondaryColor=${themeColors.primary}
                  .ariaLabel=${"Show " + remainingCount + " more social links"}
                  .size=${"small"}
                  .interactive=${false}
                >
                  ${getSocialIcon(link.platform)}
                </user-theme-button>
              `
            : nothing,
        )}
        ${remainingCount > 0
          ? html`
              <user-theme-button
                .circle=${true}
                .primaryColor=${themeColors.secondary}
                .secondaryColor=${themeColors.primary}
                .ariaLabel=${"Show " + remainingCount + " more social links"}
                .size=${"small"}
                .interactive=${false}
              >
                +${remainingCount}
              </user-theme-button>
            `
          : ""}
      </div>
    `;
  }

  protected override render() {
    // Only use server data if we don't have a locally uploaded image
    const serverBackgroundUrl = this.profileDataQuery.result.data?.backgroundImageUrl;
    // if (!this.backgroundImageUrl && serverBackgroundUrl) {
    //   this.backgroundImageUrl = serverBackgroundUrl;
    // }

    const hasBackground = false; // Boolean(this.backgroundImageUrl);

    const backgroundStyle =
      hasBackground && !this.isVideoBackground ? `background-image: url(${getAssetUrl(this.backgroundImageUrl)})` : "";

    const themeColors = this.currentThemeColors;
    const themeStyle = `
      --theme-primary-color: ${themeColors.primary};
      --theme-secondary-color: ${themeColors.secondary};
      ${backgroundStyle}
    `;

    return html`
      <div
        class="user-background-image ${hasBackground ? "has-background" : ""} ${this.isVideoBackground
          ? "is-video"
          : ""}"
        style=${themeStyle}
      >
        <media-uploader
          .showCameraOverlay=${true}
          .cameraOverlayPosition=${"top-right"}
          context="cover_image"
          currentFileUrl=${this.backgroundImageUrl ?? serverBackgroundUrl}
          currentFileType=${this.isVideoBackground ? "video" : "image"}
          uploadSubtitle=${hasBackground ? "Change cover" : "Add cover"}
          viewport="cover"
          @change=${(e: CustomEvent) => {
            this.backgroundImageUrl = e.detail.imageUrl;
            this.isVideoBackground = e.detail.fileType === "video";
            this.dispatchEvent(
              new CustomEvent("cover-image-uploaded", {
                detail: { fileId: e.detail.fileId, relativePath: e.detail.imageUrl },
                bubbles: true,
              }),
            );
          }}
        >
        </media-uploader>
        <!-- Gradient overlay -->
        <div class="gradient-overlay"></div>

        <div class="user-profile-overlay">
          <slot></slot>

          <div class="user-name">
            ${this.userHandle ?? (this.profileDataQuery.result.data?.handle || t("user.defaultGenericUserName"))}
          </div>
          ${this.renderSocialLinks()}
        </div>
      </div>
    `;
  }
}
